/**
 * The browser's own notification banner (the Notification API). Shown only
 * while the tab is hidden and the user has granted permission (spec §6): a
 * visible tab gets an in-app toast instead, and the shell never prompts for
 * permission on its own — that is a button in Settings ▸ Notifications.
 */

export type OsPermission = NotificationPermission | "unsupported";

export function osNotificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function osPermission(): OsPermission {
  return osNotificationsSupported() ? Notification.permission : "unsupported";
}

export async function requestOsPermission(): Promise<OsPermission> {
  if (!osNotificationsSupported()) return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function tabIsHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** The page's current tab icon, so a tenant's favicon brands the banner too. */
function pageIcon(): string | undefined {
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  return link?.href || undefined;
}

/**
 * Show a banner if the tab is hidden and permission is granted. Returns
 * whether one was shown. `tag` collapses repeats (one banner per chat).
 */
export function showOsNotification(opts: {
  title: string;
  body?: string;
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}): boolean {
  if (!tabIsHidden() || osPermission() !== "granted") return false;
  try {
    const n = new Notification(opts.title, {
      body: opts.body || undefined,
      tag: opts.tag,
      icon: pageIcon(),
      requireInteraction: opts.requireInteraction,
    });
    n.onclick = () => {
      window.focus();
      n.close();
      opts.onClick?.();
    };
    return true;
  } catch {
    // Some browsers (Android Chrome) only allow banners from a service
    // worker; the bell still has the notification.
    return false;
  }
}
